/**
 * Mail Config Field Visibility Configuration
 * Shows/hides fields on mail_config detail and edit based on type picklist:
 * SMTP, Outlook, Google
 *
 * Credentials section uses common fields: client_id, client_secret, tenant_id, redirect_uri
 * - SMTP: SMTP Mail, SMTP Password, SMTP Host, SMTP Port
 * - Outlook: Client Id, Client Secret, Tenant Id, Redirect URI
 * - Google: SMTP Email, Client Id, Client Secret, Redirect URI (no tenant_id)
 */

const MAIL_CONFIG_TYPE_FIELD_MAPPING = {
  SMTP: {
    visible: [
      'name',
      'type',
      'is_default',
      'smtp_mail',
      'smtp_password',
      'smtp_host',
      'smtp_port',
    ],
    required: ['name', 'smtp_mail', 'smtp_password', 'smtp_host', 'smtp_port'],
  },
  Outlook: {
    visible: [
      'name',
      'type',
      'is_default',
      'client_id',
      'client_secret',
      'tenant_id',
      'redirect_uri',
    ],
    required: ['name', 'client_id', 'client_secret', 'tenant_id'],
  },
  Google: {
    visible: [
      'name',
      'type',
      'is_default',
      'smtp_mail',
      'client_id',
      'client_secret',
      'redirect_uri',
    ],
    required: ['name', 'smtp_mail', 'client_id', 'client_secret', 'redirect_uri'],
  },
};

/**
 * Normalize config type from form/record (picklist value)
 * @param {string} configType - Raw type value (e.g. "SMTP", "smtp", "Outlook")
 * @returns {string|null} - Normalized key for mapping or null
 */
function normalizeMailConfigType(configType) {
  if (configType == null || configType === '') return null;
  const t = String(configType).trim();
  const lower = t.toLowerCase();
  if (lower === 'smtp') return 'SMTP';
  if (lower === 'outlook') return 'Outlook';
  if (lower === 'google') return 'Google';
  return t;
}

/**
 * Check if a field should be visible for the given mail config type
 * @param {string} fieldName - Field API name
 * @param {string} configType - Current type picklist value (SMTP, Outlook, Google)
 * @returns {boolean}
 */
export function isMailConfigFieldVisible(fieldName, configType) {
  if (!fieldName) return false;

  const normalizedFieldName = fieldName.toLowerCase().trim();
  const typeKey = normalizeMailConfigType(configType);

  // Always show type so user can switch; show name and is_default for all
  if (['name', 'type', 'is_default'].includes(normalizedFieldName)) {
    return true;
  }

  // No type selected: show all fields (so user can select type and then see the right set)
  if (!typeKey || !MAIL_CONFIG_TYPE_FIELD_MAPPING[typeKey]) {
    return true;
  }

  const typeConfig = MAIL_CONFIG_TYPE_FIELD_MAPPING[typeKey];
  const isInVisible = typeConfig.visible.some(
    (v) => v.toLowerCase() === normalizedFieldName
  );

  return isInVisible;
}

/**
 * Check if a field should be required for the given mail config type
 * @param {string} fieldName - Field API name
 * @param {string} configType - Current type picklist value
 * @param {boolean} baseRequired - Base required from field config
 * @returns {boolean}
 */
export function isMailConfigFieldRequired(fieldName, configType, baseRequired = false) {
  if (!fieldName || !configType) return baseRequired;

  const normalizedFieldName = fieldName.toLowerCase().trim();
  const typeKey = normalizeMailConfigType(configType);
  const typeConfig = MAIL_CONFIG_TYPE_FIELD_MAPPING[typeKey];

  if (!typeConfig) return baseRequired;

  const isRequiredByType = typeConfig.required.some(
    (r) => r.toLowerCase() === normalizedFieldName
  );

  return isRequiredByType || baseRequired;
}

/**
 * Filter fields array by mail config type visibility
 * @param {Array} fields - Array of field objects with fieldName
 * @param {string} configType - Current type value
 * @returns {Array}
 */
export function filterFieldsByMailConfigType(fields, configType) {
  if (!fields || !Array.isArray(fields)) return [];
  return fields.filter((field) =>
    isMailConfigFieldVisible(field.fieldName || field.field_name, configType)
  );
}

export default {
  isMailConfigFieldVisible,
  isMailConfigFieldRequired,
  filterFieldsByMailConfigType,
  normalizeMailConfigType,
  MAIL_CONFIG_TYPE_FIELD_MAPPING,
};
